import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";

const USER_API = process.env.REACT_APP_USER_SERVICE_URL;
const API_BASE = USER_API + "/users";

export default function ResetPasswordConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token"); // read token from URL

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); // confirm password
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setStatus("error");
      setMessage("Invalid password reset link.");
      return;
    }

    if (!newPassword.trim() || !confirmPassword.trim()) {
      setStatus("error");
      setMessage("Please fill in both password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatus("error");
      setMessage("Passwords do not match.");
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const response = await axios.post(
        `${API_BASE}/auth/reset-password/confirm`,
        {
          token,
          newPassword,
        },
      );

      const backendMessage =
        response.data?.message ||
        "Password reset successfully! Redirecting to login...";

      setStatus("success");
      setMessage(backendMessage);

      // Clear input fields
      setNewPassword("");
      setConfirmPassword("");

      // Redirect after short delay
      setTimeout(() => navigate("/login"), 3000);
    } catch (error) {
      setStatus("error");
      setMessage(
        error.response?.data?.message ||
          "Unable to reset password. The link may have expired.",
      );
    }
  };

  // Helper for live password match indicator
  const passwordsMatch = confirmPassword
    ? newPassword === confirmPassword
    : true;

  return (
    <>
      <Header hideExtras />
      <main
        style={{
          maxWidth: 420,
          margin: "4rem auto",
          padding: "2rem",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
        }}
      >
        <h2 style={{ marginBottom: "0.5rem" }}>Set a new password</h2>

        <p style={{ marginBottom: "1.5rem", color: "#6b7280" }}>
          Enter your new password below to reset your account password.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <label
            htmlFor="new-password"
            style={{ display: "block", marginBottom: 6 }}
          >
            New Password
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={status === "loading"}
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "12px",
              borderRadius: 4,
              border: "1px solid #d1d5db",
            }}
          />

          <label
            htmlFor="confirm-password"
            style={{ display: "block", marginBottom: 6 }}
          >
            Confirm Password
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={status === "loading"}
            style={{
              width: "100%",
              padding: "10px",
              marginBottom: "4px",
              borderRadius: 4,
              border: "1px solid #d1d5db",
            }}
          />

          {/* Live password match indicator */}
          {confirmPassword && (
            <p
              style={{
                marginTop: 0,
                marginBottom: "12px",
                fontSize: "0.875rem",
                color: passwordsMatch ? "#065f46" : "#dc2626",
              }}
            >
              {passwordsMatch ? "Passwords match" : "Passwords do not match"}
            </p>
          )}

          <button
            type="submit"
            disabled={status === "loading"}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: 4,
              border: "none",
              backgroundColor: status === "loading" ? "#9ca3af" : "#2563eb",
              color: "#fff",
              cursor: status === "loading" ? "not-allowed" : "pointer",
            }}
          >
            {status === "loading" ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        {message && (
          <p
            role="status"
            aria-live="polite"
            style={{
              marginTop: "1rem",
              color: status === "error" ? "#dc2626" : "#065f46",
            }}
          >
            {message}
          </p>
        )}
      </main>
    </>
  );
}
